import React from 'react'
import Fox from './Fox'
import { useTheme } from '../theme'
import { useActionsPortail } from '../contexts/navigation'
import { MVP } from '../mvp'
import useRinaFermee from '../hooks/useRinaFermee'
import RappelDossierBulle from './RappelDossierBulle'

/**
 * Carte RINA contextuelle — DESIGN-SYSTEM.md §4, forme du prototype v7.3.
 *
 * Elle sert d'EN-TETE des pages du hub : elle remplace le couple titre +
 * descriptif de page (jamais de double en-tete).
 *
 * FORME (alignee sur le prototype en S31 — point P4 du registre d'audit) :
 * le renard vit sur sa propre TUILE (52 px, degrade orange->blanc), HORS de la
 * bulle ; le texte vit dans une BULLE DE VERRE munie d'une POINTE dirigee vers
 * le renard. C'est une vraie bulle de dialogue : RINA parle, elle n'est pas
 * enfermee avec son texte dans un meme bloc.
 * Jusqu'ici le portail rendait la forme v3 : un seul bloc en verre degrade
 * creme->orange contenant renard ET texte, sans pointe.
 *
 * La bulle rejoue `bubbleIn` a CHAQUE changement de message (`key={message}`) :
 * on voit RINA reprendre la parole en changeant d'ecran.
 *
 * v7.2.4 : `title` optionnel — TITRE DE PAGE dans la bulle (Instrument Serif,
 * mis en evidence) pour les sous-pages du Compte ; les titres in-page
 * disparaissent. Cf. DESIGN-GUIDE.md § « Retour et titre de page ».
 *
 * S65 — `cle` optionnelle : rend la carte REFERMABLE, et la fermeture est
 * MEMORISEE pour cette page (retour Daniel : RINA repete la meme phrase a
 * chaque visite). Repliee, elle laisse une pastille renard qui la redeploie —
 * rien n'est perdu. Portee par page : fermer ici ne ferme pas ailleurs, ou la
 * carte porte aussi le TITRE de la page. SANS `cle`, comportement d'avant
 * (aucune croix), donc les appelants existants ne bougent pas.
 */
function RinaContextCard({ onClick, message, title, cle }) {
  // Par defaut la carte OUVRE RINA elle-meme (S48) — plus de prop onRinaClick
  // a driller ; `onClick` reste surchargable pour les cas particuliers.
  const { handleRinaClick } = useActionsPortail()
  const clic = onClick || handleRinaClick
  const { isWeb3 } = useTheme()
  const { fermee, fermer, rouvrir } = useRinaFermee(cle)

  // Repliee : une pastille renard, seule trace de RINA sur la page.
  if (cle && fermee) {
    // Le rappel du dossier exposant survit a la fermeture (S73) : seul, sous
    // la pastille — une campagne qu'on ne doit pas pouvoir rater.
    return (
      <>
        <button
          onClick={rouvrir}
          aria-label="Afficher le conseil de RINA"
          className="flex items-center justify-center mb-3 transition-transform active:scale-95"
          style={{
            width: 38, height: 38, borderRadius: 13,
            background: isWeb3
              ? 'linear-gradient(135deg, rgba(244,137,83,0.45), rgba(255,255,255,0.12))'
              : 'linear-gradient(135deg, rgba(244,137,83,0.4), rgba(255,255,255,0.5))',
            border: isWeb3 ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.9)',
          }}
        >
          <Fox size={24} />
        </button>
        <RappelDossierBulle seul />
      </>
    )
  }
  // MVP (S62) : sans conversation, la carte n'est plus un bouton — elle reste
  // l'en-tete explicatif de la page (titre + message), sans « Parler a RINA ».
  const Racine = MVP.RINA_CHAT ? 'button' : 'div'
  return (
    <Racine
      onClick={MVP.RINA_CHAT ? clic : undefined}
      className={`w-full text-left flex items-start gap-2.5 mb-3 ${MVP.RINA_CHAT ? 'transition-transform active:scale-[0.99]' : ''}`}
    >
      {/* Tuile du renard — hors bulle (prototype v7.3) */}
      <span
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 52, height: 52, borderRadius: 17,
          background: isWeb3
            ? 'linear-gradient(135deg, rgba(244,137,83,0.45), rgba(255,255,255,0.12))'
            : 'linear-gradient(135deg, rgba(244,137,83,0.4), rgba(255,255,255,0.5))',
          border: isWeb3 ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.9)',
          boxShadow: isWeb3
            ? '0 6px 16px rgba(244,137,83,0.18)'
            : 'inset 0 1px 3px rgba(255,255,255,0.95), 0 6px 16px rgba(244,137,83,0.22)',
        }}
      >
        <Fox size={34} />
      </span>

      {/* Bulle de dialogue — pointe dirigee vers le renard */}
      <div
        key={message}
        className={`relative flex-1 min-w-0 rounded-[20px] px-[15px] py-3 ${isWeb3 ? '' : 'glass-surface'}`}
        style={{
          animation: 'bubbleIn .45s ease both',
          ...(isWeb3 ? {
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          } : {}),
        }}
      >
        {/* Pointe : carre pivote a 45°, deux bords seulement — il se fond dans la bulle */}
        <span
          aria-hidden="true"
          className="absolute"
          style={{
            left: -5, top: 20, width: 12, height: 12, transform: 'rotate(45deg)',
            background: isWeb3 ? 'rgba(255,255,255,0.07)' : 'var(--c-glass)',
            borderLeft: isWeb3 ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--c-glass-border)',
            borderBottom: isWeb3 ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--c-glass-border)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          }}
        />
        {/* Croix de fermeture — `span role=button` et non `<button>` : la racine
            de la carte EST deja un bouton quand RINA_CHAT est actif, et un
            bouton dans un bouton est du HTML invalide. */}
        {cle && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Masquer le conseil de RINA"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); fermer() }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); fermer() }
            }}
            className={`absolute top-1.5 right-2 z-[1] w-6 h-6 rounded-full flex items-center justify-center text-[13px] font-bold cursor-pointer ${isWeb3 ? 'text-white/50' : 'text-ink/40'}`}
          >
            ✕
          </span>
        )}
        {title && (
          <p className={`relative font-serif text-[20px] leading-tight mb-[3px] text-ink`}>
            {title}
          </p>
        )}
        <p className={`relative leading-[1.4] ${cle ? 'pr-5' : ''} ${title
          ? `text-[12.5px] font-medium ${isWeb3 ? 'text-white/85' : 'text-ink/75'}`
          : `text-[13.5px] font-bold text-ink`}`}>
          {message}
        </p>
        {/* v7.5 (D6) : rappel prioritaire, juste apres le message de la page */}
        <RappelDossierBulle />
        {MVP.RINA_CHAT && (
          <p className="relative text-[12px] font-bold text-primary mt-[3px]">
            Parler à RINA →
          </p>
        )}
      </div>
    </Racine>
  )
}

export default RinaContextCard
